import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { merge, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  exhaustMap,
  filter,
  finalize,
  map,
  scan,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { isDigitsOnly } from '../utils/common-util';

export interface MultiSelectAutocompleteOptionDisplay {
  primary: string;
  secondary?: string;
}

export interface PagedResult<T> {
  data: T[];
  pagination: {
    offset: number;
    recordCount: number;
    totalElements: number;
  };
}

type MultiSelectAutocompleteEditorParams = ICellEditorParams & {
  placeholder?: string;
  maxLength?: number;
  minSearchTermLength?: number;
  minSearchDigitLength?: number;
  maxSelectedOptions?: number;
  loaderText?: string;

  getAutocompleteOptions: (
    searchTerm: string,
    offset?: number,
    recordCount?: number
  ) => Observable<any[] | PagedResult<any>>;

  /**
   * Unique key for option comparison.
   */
  getOptionId: (option: any) => string | number;

  /**
   * Text displayed inside the input for selected values.
   */
  displayWith: (option: any) => string;

  /**
   * Dropdown option display.
   */
  optionDisplayWith?: (option: any) => MultiSelectAutocompleteOptionDisplay;

  /**
   * Converts existing comma-separated string values into option objects.
   */
  createOptionFromValue?: (value: string) => any;

  autocompletePanelClass?: string;

  showNoResultsOption?: boolean;
  noResultsText?: string;
  isPaging?: boolean;

  /**
   * Optional callback for immediate row update.
   */
  onSelectionChange?: (
    selectedOptions: any[],
    params: ICellEditorParams
  ) => void;

  /**
   * Optional formatter for final grid value.
   * If not provided, selectedOptions array is returned.
   */
  getSelectedValue?: (selectedOptions: any[]) => any;
};

@Component({
  selector: 'lib-multi-select-autocomplete-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './multi-select-autocomplete-editor.component.html',
  styleUrls: ['./multi-select-autocomplete-editor.component.scss'],
})
export class MultiSelectAutocompleteEditorComponent
  implements ICellEditorAngularComp, AfterViewInit, OnDestroy
{
  private readonly destroyRef = inject(DestroyRef);

  private params!: MultiSelectAutocompleteEditorParams;

  public placeholder = '';
  public cellWidth = 0;
  public cellHeight = 0;
  public autocompletePanelWidth = 0;

  public searchControl = new FormControl<string>('', { nonNullable: true });
  public filteredOptions$!: Observable<any[]>;

  public autocompletePanelClass =
    'custom-autocomplete custom-autocomplete--multi-select';

  public showNoResultsOption = false;
  public noResultsText = 'No results found';
  public noResults = signal(false);
  public loader = signal(false);
  public loaderText?: string;

  public selectedOptions: any[] = [];
  public maxLength: number | null = null;
  public maxSelectedOptions?: number;

  private minSearchTermLength?: number;
  private minSearchDigitLength?: number;

  private getAutocompleteOptions!: (
    searchTerm: string,
    offset?: number,
    recordCount?: number
  ) => Observable<any[] | PagedResult<any>>;

  private getOptionId!: (option: any) => string | number;
  private displayWith!: (option: any) => string;
  private createOptionFromValue!: (value: string) => any;

  public optionDisplayWith!: (
    option: any
  ) => MultiSelectAutocompleteOptionDisplay;

  public isPaging = false;

  private currentOptions: any[] = [];
  private offset = 0;
  private readonly recordCount = 100;
  private totalElements = 0;
  private readonly loading = signal(false);

  private readonly nextPage$ = new Subject<void>();

  // Used to refresh and reposition the autocomplete panel on textarea resize.
  @ViewChild(MatAutocompleteTrigger)
  private readonly autocompleteTrigger?: MatAutocompleteTrigger;

  @ViewChild('inputWithOptions')
  private readonly textarea!: ElementRef<HTMLTextAreaElement>;

  private resizeObserver?: ResizeObserver; // Tracks textarea resize events.
  private onBodyScrollListener?: () => void; // Handles grid body scroll events.

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.textarea.nativeElement.focus();
    });

    // Keep the autocomplete overlay aligned with textarea resizing.
    this.initializeTextareaResizeObserver();
  }

  public onScroll(event: Event): void {
    if (!this.isPaging) {
      return;
    }

    const target = event.target as HTMLElement;

    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 1) {
      if (!this.loading() && this.currentOptions.length < this.totalElements) {
        this.nextPage$.next();
      }
    }
  }

  agInit(params: MultiSelectAutocompleteEditorParams): void {
    this.params = params;

    this.placeholder = params.placeholder || '';
    this.maxLength = params.maxLength ?? null;
    this.minSearchTermLength = params.minSearchTermLength;
    this.minSearchDigitLength = params.minSearchDigitLength;
    this.maxSelectedOptions = params.maxSelectedOptions;

    this.getAutocompleteOptions =
      params.getAutocompleteOptions || (() => of([]));
    this.getOptionId = params.getOptionId;
    this.displayWith = params.displayWith;
    this.loaderText = params.loaderText;

    this.createOptionFromValue =
      params.createOptionFromValue || ((value: string) => ({ value }));

    this.optionDisplayWith =
      params.optionDisplayWith ||
      ((option: any) => ({
        primary: this.displayWith(option),
      }));

    this.autocompletePanelClass =
      params.autocompletePanelClass ||
      'custom-autocomplete custom-autocomplete--multi-select';

    this.showNoResultsOption = params.showNoResultsOption || false;
    this.noResultsText = params.noResultsText || 'No results found';

    const cell = params.eGridCell;
    this.cellWidth = cell.clientWidth + 4;
    this.cellHeight = cell.clientHeight + 4;
    this.autocompletePanelWidth = this.cellWidth;

    this.selectedOptions = this.getInitialSelectedOptions(params.value);
    this.updateInputValue(false);

    this.isPaging = params.isPaging || false;

    if (this.isPaging) {
      this.filteredOptions$ = this.searchControl.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
        switchMap((inputValue) => {
          const limitedInputValue = this.limitInputToMaxCommaValues(
            inputValue || ''
          );
          const searchTerm =
            this.getSearchTermAfterLastComma(limitedInputValue);

          this.offset = 0;
          this.currentOptions = [];
          this.totalElements = 0;
          this.noResults.set(false);
          this.loading.set(false);

          const minSearchLength = this.getMinSearchLength(searchTerm);

          if (
            (this.maxSelectedOptions !== undefined &&
              this.selectedOptions.length >= this.maxSelectedOptions) ||
            (minSearchLength !== undefined &&
              searchTerm.length < minSearchLength)
          ) {
            this.loader.set(false);
            return of([]);
          }

          const initialLoad$ = this.fetchPagedOptions(
            searchTerm,
            this.offset
          ).pipe(
            catchError(() => {
              this.loader.set(false);
              this.loading.set(false);

              return of({
                data: [],
                pagination: {
                  totalElements: 0,
                  offset: 0,
                  recordCount: 0,
                },
                page: 0,
              });
            })
          );

          const subsequentPages$ = this.nextPage$.pipe(
            takeUntilDestroyed(this.destroyRef),
            filter(
              () =>
                !this.loading() &&
                this.currentOptions.length < this.totalElements
            ),
            exhaustMap(() => {
              const nextOffset = this.offset + this.recordCount;

              return this.fetchPagedOptions(searchTerm, nextOffset).pipe(
                tap(() => {
                  this.offset = nextOffset;
                }),
                catchError(() => {
                  this.loader.set(false);
                  this.loading.set(false);

                  return of({
                    data: [],
                    pagination: {
                      totalElements: this.totalElements,
                      offset: this.offset,
                      recordCount: 0,
                    },
                    page: this.offset,
                  });
                })
              );
            })
          );

          return merge(initialLoad$, subsequentPages$).pipe(
            scan((acc, response) => {
              this.totalElements = response?.pagination?.totalElements ?? 0;

              const data = response?.data ?? [];

              if (response?.page === 0) {
                return data;
              }

              return [...acc, ...data];
            }, [] as any[]),
            tap((options) => {
              this.currentOptions = options;

              if (
                this.currentOptions.length >= this.totalElements ||
                this.totalElements === 0
              ) {
                this.loader.set(false);

                const hasNoResults =
                  this.showNoResultsOption &&
                  this.currentOptions.length === 0 &&
                  this.totalElements === 0;

                this.noResults.set(hasNoResults);
              }
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      );
    } else {
      this.filteredOptions$ = this.searchControl.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
        switchMap((inputValue) => {
          const limitedInputValue = this.limitInputToMaxCommaValues(
            inputValue || ''
          );
          const searchTerm =
            this.getSearchTermAfterLastComma(limitedInputValue);

          this.noResults.set(false);
          const minSearchLength = this.getMinSearchLength(searchTerm);

          if (
            (this.maxSelectedOptions !== undefined &&
              this.selectedOptions.length >= this.maxSelectedOptions) ||
            (minSearchLength !== undefined &&
              searchTerm.length < minSearchLength)
          ) {
            this.loader.set(false);
            this.currentOptions = [];
            return of([]);
          }

          this.loader.set(true);

          return this.getAutocompleteOptions(searchTerm).pipe(
            map((response): any[] => {
              if (Array.isArray(response)) {
                return response;
              }

              return response?.data ?? [];
            }),
            tap((options: any[]) => {
              this.currentOptions = options;

              const hasNoResults =
                this.showNoResultsOption && options.length === 0;

              this.noResults.set(hasNoResults);
            }),
            catchError((): Observable<any[]> => {
              this.noResults.set(this.showNoResultsOption);
              this.currentOptions = [];
              return of([] as any[]);
            }),
            finalize(() => {
              this.loader.set(false);
            }),
            startWith([] as any[])
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      );
    }

    // Close the editor when the grid scrolls to prevent overlay misalignment.
    const listener = () => {
      this.params.api.stopEditing();
    };
    this.onBodyScrollListener = listener;
    this.params.api.addEventListener('bodyScroll', listener);
  }

  private fetchPagedOptions(
    searchTerm: string,
    offset: number
  ): Observable<PagedResult<any> & { page: number }> {
    const minSearchLength = this.getMinSearchLength(searchTerm);

    if (minSearchLength !== undefined && searchTerm.length < minSearchLength) {
      return of({
        data: [],
        pagination: {
          offset,
          recordCount: this.recordCount,
          totalElements: 0,
        },
        page: offset,
      });
    }

    this.loading.set(true);
    this.loader.set(true);

    return (
      this.getAutocompleteOptions(
        searchTerm,
        offset,
        this.recordCount
      ) as Observable<PagedResult<any>>
    ).pipe(
      map((response) => ({
        ...response,
        page: offset,
      })),
      tap(() => {
        this.loading.set(false);
        this.loader.set(false);
      }),
      catchError(() => {
        this.loading.set(false);
        this.loader.set(false);

        return of({
          data: [],
          pagination: {
            offset,
            recordCount: this.recordCount,
            totalElements: 0,
          },
          page: offset,
        });
      })
    );
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();

    if (this.onBodyScrollListener) {
      this.params.api.removeEventListener(
        'bodyScroll',
        this.onBodyScrollListener
      );
    }
  }

  public enforceInputLimit(): void {
    const value = this.searchControl.value || '';
    const limitedValue = this.limitInputToMaxCommaValues(value);

    this.syncSelectedOptionsWithInput(limitedValue);
  }

  public isSelected(option: any): boolean {
    const optionId = this.getOptionId(option);

    return this.selectedOptions.some(
      (selected) => this.getOptionId(selected) === optionId
    );
  }

  public isSelectionLimitReached(option: any): boolean {
    return (
      this.maxSelectedOptions !== undefined &&
      !this.isSelected(option) &&
      this.selectedOptions.length >= this.maxSelectedOptions
    );
  }

  public onOptionItemClick(option: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggleOption(option);
  }

  public toggleOption(option: any, event?: MatCheckboxChange): void {
    const optionId = this.getOptionId(option);
    const isChecked = event?.checked ?? !this.isSelected(option);

    if (isChecked) {
      const alreadySelected = this.selectedOptions.some(
        (selected) => this.getOptionId(selected) === optionId
      );

      if (!alreadySelected) {
        if (
          this.maxSelectedOptions !== undefined &&
          this.selectedOptions.length >= this.maxSelectedOptions
        ) {
          if (event) {
            event.source.checked = false;
          }

          return;
        }

        this.selectedOptions = [...this.selectedOptions, option];
      }
    } else {
      this.selectedOptions = this.selectedOptions.filter(
        (selected) => this.getOptionId(selected) !== optionId
      );
    }

    this.updateInputValue(false);
    this.noResults.set(false);
    this.notifySelectionChange();
  }

  public clearInput(event: Event): void {
    this.selectedOptions = [];
    this.searchControl.setValue('', { emitEvent: false });
    this.noResults.set(false);
    this.loader.set(false);
    this.notifySelectionChange();

    this.textarea.nativeElement.focus();
    event.stopPropagation();
  }

  public getValue(): any {
    return this.getSelectedGridValue();
  }

  private getSearchTermAfterLastComma(value: string): string {
    return value.split(',').pop()?.trim() || '';
  }

  private limitInputToMaxCommaValues(value: string): string {
    const commaSeparatedValues = this.getCommaSeparatedValues(value);

    if (
      this.maxSelectedOptions === undefined ||
      commaSeparatedValues.length <= this.maxSelectedOptions
    ) {
      return value;
    }

    const limitedValue = commaSeparatedValues
      .slice(0, this.maxSelectedOptions)
      .join(', ');

    this.searchControl.setValue(limitedValue, { emitEvent: false });

    return limitedValue;
  }

  private getCommaSeparatedValues(value: string): string[] {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  /**
   * Used only for manual input/backspace/delete.
   * Do not call this inside valueChanges or toggleOption,
   * otherwise new searches can remove existing selected values.
   */
  private syncSelectedOptionsWithInput(value: string): void {
    const inputValues = new Set(
      this.getCommaSeparatedValues(value).map((item) => item.toLowerCase())
    );

    const previousSelectedOptionsLength = this.selectedOptions.length;

    this.selectedOptions = this.selectedOptions.filter((option) => {
      const selectedValue = this.displayWith(option)?.trim();

      return selectedValue && inputValues.has(selectedValue.toLowerCase());
    });

    if (this.selectedOptions.length !== previousSelectedOptionsLength) {
      this.notifySelectionChange();
    }
  }

  private updateInputValue(emitEvent: boolean): void {
    const selectedText = this.selectedOptions
      .map((option) => this.displayWith(option))
      .filter(Boolean)
      .join(', ');

    this.searchControl.setValue(selectedText, { emitEvent });
  }

  private notifySelectionChange(): void {
    const field = this.params.colDef.field;
    const value = this.getSelectedGridValue();

    if (this.params.onSelectionChange) {
      this.params.onSelectionChange(this.selectedOptions, this.params);
    } else if (field && this.params.node) {
      this.params.node.setDataValue(field, value);
    }

    this.params.api.refreshCells({
      rowNodes: [this.params.node],
      force: true,
    });

    this.params.context?.onRowValidationChanged?.();
  }

  private getSelectedGridValue(): any {
    if (this.params.getSelectedValue) {
      return this.params.getSelectedValue(this.selectedOptions);
    }

    return this.selectedOptions;
  }

  private getInitialSelectedOptions(value: any): any[] {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return this.maxSelectedOptions === undefined
        ? value
        : value.slice(0, this.maxSelectedOptions);
    }

    if (typeof value === 'string') {
      let values = value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

      if (this.maxSelectedOptions !== undefined) {
        values = values.slice(0, this.maxSelectedOptions);
      }

      return values.map((item) => this.createOptionFromValue(item));
    }

    return this.maxSelectedOptions === 0 ? [] : [value];
  }

  private getMinSearchLength(searchTerm: string): number | undefined {
    if (this.minSearchDigitLength !== undefined && isDigitsOnly(searchTerm)) {
      return this.minSearchDigitLength;
    }

    return this.minSearchTermLength;
  }

  /**
   * Angular Material does not re-size an already open autocomplete overlay when the textarea is manually resized.
   * Re-opening the panel forces the overlay width to be recalculated.
   */
  private refreshAutocompletePanel(): void {
    if (!this.autocompleteTrigger?.panelOpen) return;

    this.autocompleteTrigger.closePanel();
    queueMicrotask(() => {
      this.autocompleteTrigger?.openPanel();
    });
  }

  // To Keep the autocomplete panel width synchronized with the textarea when the user manually resizes it.
  private initializeTextareaResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      const width = this.textarea.nativeElement.getBoundingClientRect().width;
      this.autocompletePanelWidth = width;
      this.refreshAutocompletePanel();
    });

    this.resizeObserver.observe(this.textarea.nativeElement);
  }
}
